import React from 'react';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SectionCard } from '../../components/Section';
import { httpStatusCodes } from '../../constants/httpStatusCodes';
import { useFetchDerayahOrdersByMadkholOrderIdQuery } from '../../redux/features/derayah/derayah.apiSlice';
import { DerayahDetails } from './components/derayah';

interface DerayahTabProps {
  orderId: string;
}

interface DerayahQueryError {
  status?: number | string;
  data?: { message?: string; traceId?: string };
}

function DerayahTabSkeleton() {
  return (
    <Stack spacing="16px">
      <Skeleton variant="rounded" height={180} />
      <Skeleton variant="rounded" height={140} />
      <Skeleton variant="rounded" height={120} />
    </Stack>
  );
}

export function DerayahTab({ orderId }: DerayahTabProps) {
  const { data: orders = [], isLoading, error } = useFetchDerayahOrdersByMadkholOrderIdQuery(orderId);

  if (isLoading) {
    return <DerayahTabSkeleton />;
  }

  // 401 is handled globally by the re-auth base query.
  if (error) {
    const { status, data } = error as DerayahQueryError;

    if (status === httpStatusCodes.forbidden) {
      return <Alert severity="warning">You do not have permission to view Derayah orders.</Alert>;
    }

    return (
      <Alert severity="error">
        {data?.message || 'Failed to load the Derayah order.'}
        {data?.traceId && (
          <Typography component="div" variant="caption" sx={{ mt: '4px' }}>
            Trace ID: {data.traceId}
          </Typography>
        )}
      </Alert>
    );
  }

  const [order] = orders;

  if (!order) {
    return (
      <SectionCard>
        <Typography color="text.secondary">No Derayah execution found for this order</Typography>
      </SectionCard>
    );
  }

  return <DerayahDetails order={order} />;
}
